import React, { useEffect, useRef } from "react";
import { Message } from "ai";
import { ThreadMessage } from "@src/features/assistantChat/ThreadMessage";
import { useChatContext } from "@src/features/assistantChat/ChatRuntimeProvider";
import ChatErrorMessage from "@src/features/aiAssist/uiComponents/ChatErrorMessage";
import { SuggestedQuestionsDisplay } from "@src/features/assistantChat/SuggestedQuestionsDisplay";
import Text from "@src/lib/ui/Text";
import { cn } from "@src/lib/utils/cn";

export interface ChatThreadProps {
  messages: Message[];
  emptyStateTitle?: string;
  emptyStateTitle2?: string;
  emptyStateSubtitle?: string;
  className?: string;
  emptyStateFooter?: React.ReactNode;
  emptyStateContent?: React.ReactNode;
  onSelectCanvas?: (canvasContent: string) => void;
  threadActionButtonHandlers?: {
    [key: string]: () => void;
  };
  chatAvatarName?: string;
  suggestedQuestionsProps?: {
    enabled: boolean;
    onQuestionSelect: (questionId: string, questionText: string) => void;
  };
}

export const ChatThread: React.FC<ChatThreadProps> = ({
  messages,
  emptyStateTitle = "How can I help you today?",
  emptyStateTitle2,
  emptyStateSubtitle,
  className = "",
  onSelectCanvas,
  threadActionButtonHandlers,
  chatAvatarName,
  suggestedQuestionsProps,
  emptyStateFooter,
  emptyStateContent,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { error, clearError, submitMessage } = useChatContext();

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const isEmpty = messages.length === 0;

  return (
    <div className={cn("flex h-full min-w-0 flex-col", className)}>
      <div
        className={cn(
          "flex min-h-0 min-w-0 flex-1 flex-col",
          isEmpty && "justify-center",
        )}
      >
        <div
          className={cn(
            "flex w-full min-w-0 max-w-full flex-col",
            isEmpty && emptyStateContent
              ? "mx-auto max-w-2xl gap-5 px-2"
              : undefined,
            isEmpty && !emptyStateContent ? "items-center gap-1" : undefined,
            !isEmpty ? "gap-1" : undefined,
          )}
        >
          {isEmpty &&
            (emptyStateContent ? (
              <>
                {emptyStateContent}
                {suggestedQuestionsProps?.enabled && (
                  <div className="w-full">
                    <SuggestedQuestionsDisplay
                      onQuestionSelect={
                        suggestedQuestionsProps.onQuestionSelect
                      }
                      isVisible={true}
                    />
                  </div>
                )}
                {emptyStateFooter}
              </>
            ) : (
              <div className="flex w-full min-w-0 max-w-full flex-col items-center justify-center py-16">
                <Text variant="body-title" className="text-center">
                  {emptyStateTitle}
                </Text>
                {emptyStateTitle2 && (
                  <Text variant="body-title" className="text-center">
                    {emptyStateTitle2}
                  </Text>
                )}
                {emptyStateSubtitle && (
                  <p className="mt-2 text-center text-sm text-muted-foreground">
                    {emptyStateSubtitle}
                  </p>
                )}
                {suggestedQuestionsProps?.enabled && (
                  <div className="mt-8 w-full min-w-0 max-w-full">
                    <SuggestedQuestionsDisplay
                      onQuestionSelect={
                        suggestedQuestionsProps.onQuestionSelect
                      }
                      isVisible={true}
                    />
                  </div>
                )}
                {emptyStateFooter}
              </div>
            ))}
          {!isEmpty &&
            messages.map((message, index) => {
              const isLastMessage = index === messages.length - 1;

              return (
                <ThreadMessage
                  key={message.id}
                  message={message}
                  messageIndex={index}
                  isLastMessage={isLastMessage}
                  onSelectCanvas={onSelectCanvas}
                  threadActionButtonHandlers={threadActionButtonHandlers}
                  chatAvatarName={chatAvatarName}
                />
              );
            })}
          {/* Show error message if there's an error */}
          {error && (
            <ErrorMessageDisplay
              messages={messages}
              clearError={clearError}
              submitMessage={submitMessage}
            />
          )}
          <div ref={messagesEndRef} className="h-4" />
        </div>
      </div>
    </div>
  );
};

export default ChatThread;

interface ErrorMessageDisplayProps {
  messages: Message[];
  clearError: () => void;
  submitMessage: (input: {
    role: "user";
    content: string;
    customMessageData?: any;
  }) => void;
}

const ErrorMessageDisplay: React.FC<ErrorMessageDisplayProps> = ({
  messages,
  clearError,
  submitMessage,
}) => {
  // Find the last user message for retry functionality
  const lastUserMessage = [...messages]
    .reverse()
    .find((msg) => msg.role === "user");

  const handleRetry = lastUserMessage
    ? () => {
        // Clear the error first, then retry by resending the last user message
        clearError();
        // Use setTimeout to ensure error is cleared before submitting
        setTimeout(() => {
          submitMessage({
            role: "user",
            content: lastUserMessage.content,
            customMessageData: (lastUserMessage as any).customMessageData,
          });
        }, 0);
      }
    : undefined;

  return (
    <div className="w-full max-w-2xl">
      <ChatErrorMessage onRetry={handleRetry} />
    </div>
  );
};
